import { useEffect, useState } from "react";
import {
  PageABTestCookieName,
  activeExperiments,
  ExperimentId,
  ExperimentTag,
  SegmentId,
  ComponentABTestCookieName
} from "../experiments";
import { decodeHistory } from "../history";
import { allActiveExperimentTags } from "src/ab-test-experiments/client/experiment-tag";
import { stringPresent } from "src/utils/strings";
import { getCookie } from "src/utils/cookies";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import useIsMounted from "src/useIsMounted";

export type ABTestDomAttr = {
  ["abtest-experiment"]: string;
  ["abtest-segment"]: string;
};

type UseNotifyAnalyticsForComponentTestShownArgs = {
  enabled: boolean;
  experimentId: ExperimentId;
};
export const useNotifyAnalyticsForComponentTestShown = ({
  enabled,
  experimentId
}: UseNotifyAnalyticsForComponentTestShownArgs) => {
  const tag = useExperimentTag(experimentId);

  useEffect(() => {
    if (enabled && tag?.experimentId && tag?.segmentId)
      notifyAnalyticsForComponentTestShown(tag.experimentId, tag.segmentId);
  }, [enabled, tag?.experimentId, tag?.segmentId]);

  return tag;
};

const notifyAnalyticsForComponentTestShown = (
  experimentId: string,
  segmentId: SegmentId
) => {
  window.wspr?.P("ab-component-test-shown", {
    experimentId: experimentId,
    segmentId: segmentId
  });
};

// eslint-disable-next-line import/no-unused-modules
export function useExperimentTag(
  experimentId: ExperimentId
): ExperimentTag | undefined {
  const { countryCode } = useGeolocation();
  const isMounted = useIsMounted();
  const [experiment, setExperiment] = useState<ExperimentTag | undefined>(
    undefined
  );

  useEffect(() => {
    if (!isMounted) return;

    const segmentId = getExperimentSegment(experimentId, countryCode);
    if (segmentId) setExperiment({ experimentId, segmentId });
    else setExperiment(undefined);
  }, [isMounted, experimentId, countryCode]);

  return experiment;
}

export function useABTestDomAttr(
  experimentId: ExperimentId
): ABTestDomAttr | undefined {
  const experiment = useExperimentTag(experimentId);
  return toABTestDomAttr(experiment);
}

function toABTestDomAttr(
  e: ExperimentTag | undefined
): ABTestDomAttr | undefined {
  if (!e) return undefined;
  return {
    ["abtest-experiment"]: e.experimentId,
    ["abtest-segment"]: e.segmentId || "a"
  };
}

function getExperimentSegment(
  experimentId: ExperimentId,
  country: string | undefined
): SegmentId | undefined {
  const path = window.location.href;
  const queryString = window.location.search;
  const queryStringParams = new URLSearchParams(queryString);
  const overrideABTestSegment = queryStringParams.get(
    `${experimentId}-segment`
  );
  if (stringPresent(overrideABTestSegment))
    return overrideABTestSegment == "b" ? "b" : "a";

  const history = experimentTagsFromCookie();

  const allActive = allActiveExperimentTags(
    activeExperiments,
    history,
    path,
    country
  );
  const found = allActive.find((e) => e.experimentId === experimentId);

  return found?.segmentId;
}

const experimentTagsFromCookie = (): ExperimentTag[] => {
  const pageEncodedCookie = getCookie(PageABTestCookieName);
  const componentEncodedCookie = getCookie(ComponentABTestCookieName);

  try {
    return [
      ...decodeHistory(pageEncodedCookie || ""),
      ...decodeHistory(componentEncodedCookie || "")
    ];
  } catch (_e) {
    return [];
  }
};
